import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Menu, MoveRight, ShoppingCart, Award, Globe, TrendingUp, ClipboardList, X } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';
import { BrandLogo } from '../../components/domain/brand-logo';

type Product = {
  id: string;
  name: string;
  brand: string;
  brand_en?: string | null;
  product_type: string;
  alcohol_percentage: number;
  vintage_year?: number | null;
  positioning?: string | null;
  description?: string | null;
  flavor_profile?: string | null;
  cover_image?: string | null;
  is_featured?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type PurchaseLink = {
  id: string;
  platform_name: string;
  platform_name_en?: string | null;
  url: string;
  platform_icon?: string | null;
  sort_order?: number | null;
  is_active?: boolean | null;
  product_id: string;
  created_at?: string | null;
  updated_at?: string | null;
};

type BartenderProgram = {
  id: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  join_requirement: string;
  benefit_travel?: string | null;
  benefit_travel_condition?: string | null;
  benefit_overseas?: string | null;
  benefit_overseas_cities?: string | null;
  benefit_subsidy?: string | null;
  benefit_subsidy_rate?: number | null;
  is_active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type ApiListResponse<T> = {
  success?: boolean;
  data?: {
    items?: T[] | null;
    total?: number;
    page?: number;
    pageSize?: number;
    totalPages?: number;
  } | null;
  message?: string;
};

type ProductDisplay = {
  title: string;
  description: string;
  alcoholText: string;
  categoryText: string;
  image: string;
};

type ProgramBenefit = {
  key: string;
  title: string;
  headline: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
};

type ProgramDisplay = {
  title: string;
  subtitle: string;
  joinRequirementTitle: string;
  joinRequirementText: string;
  benefits: ProgramBenefit[];
};

type PurchaseLinkDisplay = {
  id: string;
  label: string;
  url: string;
};

const publicFetcher = async <T,>(url: string): Promise<ApiListResponse<T>> => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.error?.message || data?.message || '请求失败');
  }

  return (data ?? {}) as ApiListResponse<T>;
};

const fallbackProduct: Product = {
  id: 'fallback-product',
  name: '元酒梦2021君系列',
  brand: '元酒梦',
  brand_en: 'YUAN JIU MENG',
  product_type: '黄酒',
  alcohol_percentage: 15,
  vintage_year: 2021,
  positioning: '东方黄酒，中国版味美思',
  description:
    '元酒梦2021「君系列」是一款高品质黄酒，但其纯净的酒质与深邃的“鲜”味，使其成为调酒师手中完美的“中国版味美思”。它能精准衔接各种基酒，为鸡尾酒注入来自东方的草本底蕴。',
  flavor_profile:
    '拥有中国版味美思的衔接力，却带着黄酒独有的鲜与温润，是跨越东西方的味觉桥梁。',
  cover_image:
    'https://images.unsplash.com/photo-1569529465841-dfecdab7503b?auto=format&fit=crop&q=80&w=800',
  is_featured: true,
};

const fallbackProgram: BartenderProgram = {
  id: 'fallback-program',
  title: '调酒师共创计划',
  subtitle: 'BARTENDER CO-CREATION PROGRAM',
  description:
    '与品牌共同拓展东方黄酒在鸡尾酒中的边界，为每一位追求极致风味的调酒师提供舞台。',
  join_requirement:
    '门店正式酒单中必须包含至少一款使用了元酒梦君系列的原创鸡尾酒',
  benefit_travel: '客座差旅报销',
  benefit_travel_condition: '门店单次进货满2箱「君系列」，即可申请报销客座调酒师的全额往返差旅路费。',
  benefit_overseas: '海外客座机遇',
  benefit_overseas_cities: '日本银座、新加坡顶奢酒店及曼谷高端酒吧',
  benefit_subsidy: '专项活动补贴',
  benefit_subsidy_rate: 10,
  is_active: true,
};

const fallbackPurchaseLinks: PurchaseLink[] = [
  {
    id: 'purchase-page',
    platform_name: '站内下单',
    platform_name_en: 'YUAN JIU MENG',
    url: '/purchase',
    product_id: 'fallback-product',
    sort_order: 0,
    is_active: true,
  },
  {
    id: 'amazon',
    platform_name: '亚马逊',
    platform_name_en: 'AMAZON',
    url: 'https://www.amazon.com',
    product_id: 'fallback-product',
    sort_order: 1,
    is_active: true,
  },
  {
    id: 'tmall',
    platform_name: '天猫',
    platform_name_en: 'TMALL',
    url: 'https://www.tmall.com',
    product_id: 'fallback-product',
    sort_order: 2,
    is_active: true,
  },
  {
    id: 'jd',
    platform_name: '京东',
    platform_name_en: 'JD.COM',
    url: 'https://www.jd.com',
    product_id: 'fallback-product',
    sort_order: 3,
    is_active: true,
  },
];

const navItems = [
  { label: '首页', href: '#hero' },
  { label: '关于', href: '#about' },
  { label: '君系列', href: '#product' },
  { label: '寻找可交换客座的酒吧', href: '/bar-venues' },
  { label: '调酒师合作', href: '#partner' },
  { label: '基础配方', href: '/recipes' },
  { label: '会员中心', href: '/member' },
  { label: '购买下单', href: '/purchase' },
];

type HonorWallItem = {
  honor: string;
  works: string[];
};

const honorWallItems: HonorWallItem[] = [
  {
    honor: '2025CMBC冠军',
    works: ['紫气东来'],
  },
  {
    honor: '2026蔗园朗姆酒热带挑战赛 东区冠军',
    works: ['绍兴以南', '风味探险家'],
  },
];

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: '"Noto Serif SC", "Songti SC", serif',
};

const bodyFontStyle: React.CSSProperties = {
  fontFamily: 'Montserrat, "PingFang SC", "Microsoft YaHei", sans-serif',
};

const splitDisplayItems = (text: string): string[] => {
  const normalizedText = text?.trim?.() ?? '';

  if (!normalizedText) {
    return [];
  }

  const hasSemicolon = normalizedText.includes('；');
  const items = hasSemicolon
    ? normalizedText
        .split('；')
        .map((item) => item?.trim?.() ?? '')
        .filter(Boolean)
    : [normalizedText];

  return items;
};

const mapProductToDisplay = (product: Product): ProductDisplay => {
  const title = product?.positioning?.trim?.() || '东方黄酒，中国版味美思';
  const description =
    product?.description?.trim?.() ||
    '以黄酒之骨，衔接基酒与草本香气，为鸡尾酒注入来自东方的鲜与温润。';
  const alcoholText = `${product?.alcohol_percentage ?? 15}%`;
  const categoryText = product?.product_type?.trim?.() || '黄酒';
  const image = product?.cover_image?.trim?.() || fallbackProduct.cover_image || '';

  return {
    title,
    description,
    alcoholText,
    categoryText,
    image,
  };
};

const mapProgramToDisplay = (program: BartenderProgram): ProgramDisplay => {
  const benefitRate = `${program?.benefit_subsidy_rate ?? 10}%`;

  return {
    title: program?.title?.trim?.() || '调酒师共创计划',
    subtitle: program?.subtitle?.trim?.() || 'BARTENDER CO-CREATION PROGRAM',
    joinRequirementTitle: '共创加入要求',
    joinRequirementText:
      program?.join_requirement?.trim?.() || fallbackProgram.join_requirement || '',
    benefits: [
      {
        key: 'travel',
        title: '01 / 客座差旅报销',
        headline: '进货2箱（12瓶）即可报销差旅费用',
        description:
          '针对对象：非元酒梦品牌合作酒吧、其他品牌邀请的客座活动。',
        icon: Award,
      },
      {
        key: 'overseas',
        title: '02 / 海外客座机遇',
        headline: '随品牌共谋海外',
        description:
          `入选计划的调酒师可参与${program?.benefit_overseas_cities?.trim?.() || fallbackProgram.benefit_overseas_cities || '国际合作城市'}等国际交流，展现东方调配魅力。`,
        icon: Globe,
      },
      {
        key: 'subsidy',
        title: '03 / 专项活动补贴',
        headline: `${benefitRate} 进货额补贴`,
        description:
          '专项活动补贴：每年根据门店前一年进货额，于2月发放专项活动补贴，补贴金额为进货额的10%',
        icon: TrendingUp,
      },
      {
        key: 'guest-shift',
        title: '04 / 交换客座',
        headline: '两天一夜文化交换',
        description:
          '在所有符合条件的酒吧内寻找你的交换对象，每15箱进货可以获得品牌覆盖差旅的两天一夜的交换客座机会，给自己的酒吧带来新的文化风味，也给自己探索文化的机会。；与元酒梦合作的鸡尾酒酒吧，在开展交换客座活动时，元酒梦品牌不会要求额外进货，活动的开展应根据交换客座双方店内现有存酒进行，除必要品的购买，不鼓励双方为了活动而囤酒。',
        icon: ClipboardList,
      },
    ],
  };
};

const mapPurchaseLinks = (links: PurchaseLink[]): PurchaseLinkDisplay[] => {
  return (links ?? []).map((item) => ({
    id: item?.id ?? `${item?.platform_name ?? 'link'}-${item?.sort_order ?? 0}`,
    label: `${item?.platform_name?.trim?.() || '购买平台'}${item?.platform_name_en?.trim?.() ? ` ${item.platform_name_en.trim()}` : ''}`,
    url: item?.url?.trim?.() || '#',
  }));
};

const isInternalPath = (url: string) => (url?.startsWith?.('/') ?? false) && !(url?.startsWith?.('//') ?? false);

const Home: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');

  const { data: productResponse, error: productError, isLoading: productLoading } = useSWR<ApiListResponse<Product>>(
    '/api/products?page=1&pageSize=20',
    publicFetcher,
  );
  const { data: linkResponse, error: linkError, isLoading: linkLoading } = useSWR<ApiListResponse<PurchaseLink>>(
    '/api/purchase_links?page=1&pageSize=20',
    publicFetcher,
  );
  const { data: programResponse, error: programError, isLoading: programLoading } = useSWR<ApiListResponse<BartenderProgram>>(
    '/api/bartender_programs?page=1&pageSize=20',
    publicFetcher,
  );

  const productItems = productResponse?.data?.items ?? [];
  const linkItems = linkResponse?.data?.items ?? [];
  const programItems = programResponse?.data?.items ?? [];

  const activeProduct = useMemo(() => {
    return (productItems ?? []).find((item) => item?.is_featured) ?? productItems?.[0] ?? fallbackProduct;
  }, [productItems]);

  const activeProgram = useMemo(() => {
    return (programItems ?? []).find((item) => item?.is_active) ?? programItems?.[0] ?? fallbackProgram;
  }, [programItems]);

  const activeLinks = useMemo(() => {
    const filtered = (linkItems ?? [])
      .filter((item) => item?.is_active ?? true)
      .sort((a, b) => (a?.sort_order ?? 999) - (b?.sort_order ?? 999));

    return filtered.length > 0 ? filtered : fallbackPurchaseLinks;
  }, [linkItems]);

  const productDisplay = mapProductToDisplay(activeProduct);
  const programDisplay = mapProgramToDisplay(activeProgram);
  const purchaseDisplays = mapPurchaseLinks(activeLinks);
  const joinRequirementItems = useMemo(() => {
    return splitDisplayItems(programDisplay?.joinRequirementText ?? '');
  }, [programDisplay]);

  const hasApiIssue = Boolean(productError || linkError || programError);
  const isRefreshing = Boolean(productLoading || linkLoading || programLoading);
  const productEmpty = (productItems ?? []).length === 0;
  const programEmpty = (programItems ?? []).length === 0;
  const linkEmpty = (linkItems ?? []).length === 0;

  const handleSubscribe = () => {
    const normalizedEmail = email?.trim?.() ?? '';
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);

    if (!isValidEmail) {
      toast.error('请输入有效邮箱');
      return;
    }

    toast.success('订阅成功，感谢关注');
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-[#000000] text-white" style={bodyFontStyle}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;700&family=Montserrat:wght@300;400;500;700&display=swap');`}</style>

      <nav className="fixed left-0 top-0 z-50 w-full border-b border-white/10 bg-black/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-5 md:px-10">
          <BrandLogo className="shrink-0" iconClassName="h-10 w-10 sm:h-11 sm:w-11" titleClassName="text-lg sm:text-xl md:text-2xl" subtitleClassName="text-[9px] sm:text-[10px]" />

          <div className="hidden items-center gap-7 text-xs font-medium tracking-[0.2em] text-white/65 md:flex">
            {navItems.map((item) => {
              const isRouteLink = item?.href?.startsWith?.('/') ?? false;

              if (isRouteLink) {
                return (
                  <Link key={item.href} to={item.href} className="transition-colors hover:text-white">
                    {item.label}
                  </Link>
                );
              }

              return (
                <a key={item.href} href={item.href} className="transition-colors hover:text-white">
                  {item.label}
                </a>
              );
            })}
          </div>

          <div className="flex items-center gap-3 md:gap-6">
            <Link
              to="/purchase"
              className="hidden items-center gap-2 bg-[#FFC000] px-5 py-2.5 text-xs font-bold tracking-[0.12em] text-black transition-colors hover:bg-[#d4a100] md:flex"
            >
              <ShoppingCart className="h-4 w-4" />
              立即购买
            </Link>
            <button
              type="button"
              className="text-white md:hidden"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label={mobileMenuOpen ? '关闭导航菜单' : '打开导航菜单'}
            >
              {mobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-white/10 bg-black/95 px-5 py-4 md:hidden">
            <div className="mb-4">
              <BrandLogo iconClassName="h-9 w-9" titleClassName="text-base" subtitleClassName="text-[9px]" />
            </div>
            <div className="flex flex-col gap-4 text-sm text-white/80">
              {navItems.map((item) => {
                const isRouteLink = item?.href?.startsWith?.('/') ?? false;

                if (isRouteLink) {
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="transition-colors hover:text-[#FFC000]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  );
                }

                return (
                  <a
                    key={item.href}
                    href={item.href}
                    className="transition-colors hover:text-[#FFC000]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                );
              })}
              <Link
                to="/purchase"
                className="inline-flex items-center justify-center gap-2 bg-[#FFC000] px-4 py-3 font-bold text-black"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ShoppingCart className="h-4 w-4" />
                立即购买
              </Link>
            </div>
          </div>
        )}
      </nav>

      <main>
        <section id="hero" className="relative flex min-h-[800px] items-center justify-center overflow-hidden bg-black px-5 pt-28 text-center md:px-10">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&q=80&w=2000"
              alt="元酒梦黄酒氛围"
              className="h-full w-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/20 to-black/85" />
          </div>

          <div className="relative z-10 max-w-5xl">
            <div className="mb-5 inline-block border border-[#FFC000] px-4 py-1.5 text-[11px] font-bold tracking-[0.35em] text-[#FFC000] md:text-xs">
              2021 年份 · 黄酒新作
            </div>
            <h1 className="mb-5 text-5xl leading-tight tracking-[0.18em] text-white md:text-7xl" style={sectionTitleStyle}>
              元酒梦 · 君系列
            </h1>
            <div className="mb-10 text-base tracking-[0.45em] text-white/80 md:text-2xl" style={sectionTitleStyle}>
              以黄酒之骨 · 赋鸡尾酒之鲜
            </div>
            <div className="flex flex-col items-center justify-center gap-5 md:flex-row">
              <a
                href="#flavor"
                className="w-full max-w-[240px] bg-[#FFC000] px-6 py-4 text-sm font-bold tracking-[0.2em] text-black transition-all hover:bg-[#d4a100]"
              >
                探索风味
              </a>
              <a
                href="#partner"
                className="w-full max-w-[240px] border border-white/50 px-6 py-4 text-sm font-bold tracking-[0.15em] text-white transition-all hover:bg-white hover:text-black"
              >
                了解更多
              </a>
            </div>
          </div>
        </section>

        <section id="about" className="bg-[#080808] px-5 py-5 md:px-10">
          <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-4 border-y border-white/10 py-4 text-sm text-white/65">
            <span>专为调酒师打造的东方风味基酒</span>
            <span>深色基调 · 金色点缀 · 高级风味表达</span>
            <span>中国版味美思的全新想象</span>
          </div>
        </section>

        <section id="brand-message" className="bg-[#080808] px-5 pb-10 md:px-10 md:pb-16">
          <div className="mx-auto max-w-[1200px] border border-[#FFC000]/25 bg-gradient-to-br from-[#17120a] via-[#101010] to-[#080808] p-8 shadow-[0_24px_80px_rgba(0,0,0,0.35)] md:p-12">
            <div className="mb-5 inline-block border border-[#FFC000]/60 px-4 py-1 text-[11px] tracking-[0.35em] text-[#FFC000]">
              创始人寄语
            </div>
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.6fr] lg:items-start">
              <h2 className="text-3xl leading-tight text-white md:text-5xl" style={sectionTitleStyle}>
                一起把中国的酒带向更远的鸡尾酒世界
              </h2>
              <div className="space-y-4 border-l border-[#FFC000]/35 pl-6 text-base leading-9 text-white/78 md:text-lg">
                <p>作为品牌创始人，我一直觉得中国酒很好，中国的调酒师也很好。</p>
                <p>但现状是：很多酒吧不惜重金请海外调酒师进来，却没有能力组织中国的调酒师出去看看。</p>
                <p>我们跟国外的差距到底有多大？或者说，我们其实已经足够优秀了？</p>
                <p>也很多调酒师觉得，自己到了职业天花板。</p>
                <p>是时候了——用鸡尾酒的方式，把中国的酒推向世界。</p>
                <p>也是时候，有人来搭建国内外鸡尾酒交流的通道了。</p>
                <p className="font-semibold text-[#FFC000]">我可能不是第一个，但我一定是最坚定的那个同行者。</p>
              </div>
            </div>
          </div>
        </section>

        <section id="product" className="bg-[#111111] px-5 py-24 md:px-10">
          <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-14 md:flex-row md:gap-16">
            <div className="flex w-full justify-center md:w-1/2">
              <div className="relative w-[300px] md:w-[400px]">
                <div className="absolute inset-0 rounded-[200px] bg-[#FFC000]/5 blur-[100px]" />
                {isRefreshing && productEmpty ? (
                  <Skeleton className="relative z-10 h-[420px] w-full rounded-[24px] bg-white/10" />
                ) : (
                  <img
                    src={productDisplay.image}
                    alt={activeProduct?.name ?? '元酒梦产品图'}
                    className="relative z-10 h-[420px] w-full rounded-[24px] object-cover shadow-[0_30px_80px_rgba(0,0,0,0.45)] md:h-[540px]"
                  />
                )}
              </div>
            </div>

            <div className="w-full md:w-1/2">
              <div className="mb-5 inline-block border border-[#FFC000]/60 px-4 py-1 text-[11px] tracking-[0.35em] text-[#FFC000]">
                君系列产品
              </div>
              <h2 className="mb-5 text-4xl text-white md:text-5xl" style={sectionTitleStyle}>
                {activeProduct?.name ?? fallbackProduct.name}
              </h2>
              <div className="mb-4 text-lg tracking-[0.12em] text-[#FFC000]">{productDisplay.title}</div>
              <div className="mb-8 max-w-xl text-base leading-8 text-white/72 md:text-lg">{productDisplay.description}</div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { label: '产品类型', value: productDisplay.categoryText },
                  { label: '酒精度', value: productDisplay.alcoholText },
                  { label: '年份', value: `${activeProduct?.vintage_year ?? fallbackProduct.vintage_year ?? 2021}` },
                ].map((item) => (
                  <div key={item.label} className="border border-white/10 bg-white/[0.02] p-5">
                    <div className="mb-2 text-xs tracking-[0.25em] text-white/45">{item.label}</div>
                    <div className="text-lg text-white">{item.value}</div>
                  </div>
                ))}
              </div>

              {(hasApiIssue || productEmpty) && (
                <div className="mt-5 text-sm text-[#d6ba63]">当前已为你展示品牌默认产品内容。</div>
              )}
            </div>
          </div>
        </section>

        <section id="partner" className="bg-[#080808] px-5 py-24 md:px-10">
          <div className="mx-auto max-w-[1200px]">
            <div className="mb-14 max-w-3xl">
              <div className="mb-5 inline-block border border-[#FFC000]/60 px-4 py-1 text-[11px] tracking-[0.35em] text-[#FFC000]">
                调酒师合作
              </div>
              <h2 className="mb-5 text-4xl text-white md:text-5xl" style={sectionTitleStyle}>
                {programDisplay.title}
              </h2>
              <div className="mb-4 text-sm tracking-[0.4em] text-[#FFC000]">{programDisplay.subtitle}</div>
              <div className="text-base leading-8 text-white/70 md:text-lg">{activeProgram?.description?.trim?.() || fallbackProgram.description}</div>
            </div>

            <div className="mb-12 grid gap-8 lg:grid-cols-[1.2fr_1fr]">
              <div className="border border-white/10 bg-white/[0.02] p-8 md:p-10">
                <div className="mb-4 text-xs tracking-[0.3em] text-[#FFC000]">{programDisplay.joinRequirementTitle}</div>
                {isRefreshing && programEmpty ? (
                  <div className="space-y-3">
                    <Skeleton className="h-5 w-full bg-white/10" />
                    <Skeleton className="h-5 w-[90%] bg-white/10" />
                    <Skeleton className="h-5 w-[78%] bg-white/10" />
                  </div>
                ) : (
                  <ul className="space-y-3 text-base leading-8 text-white/78">
                    {joinRequirementItems.map((item, index) => (
                      <li key={`${item}-${index}`} className="flex items-start gap-3">
                        <span className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#FFC000]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="border border-[#FFC000]/20 bg-[#141414] p-8 md:p-10">
                <div className="mb-4 text-xs tracking-[0.3em] text-[#FFC000]">合作亮点</div>
                <div className="space-y-5">
                  {[
                    '围绕黄酒调酒场景共创原创配方',
                    '面向高端酒吧、酒店与国际客座渠道',
                    '以长期合作支持品牌与调酒师共同成长',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3 text-white/75">
                      <ClipboardList className="mt-0.5 h-5 w-5 text-[#FFC000]" />
                      <span className="leading-7">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {programDisplay.benefits.map((benefit) => {
                const BenefitIcon = benefit.icon;
                const benefitDescriptionItems = splitDisplayItems(benefit?.description ?? '');

                return (
                  <div key={benefit.key} className="group border border-white/10 bg-[#111111] p-8 transition-all hover:-translate-y-1 hover:border-[#FFC000]/40">
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full border border-[#FFC000]/30 bg-[#FFC000]/10 text-[#FFC000]">
                      <BenefitIcon className="h-5 w-5" />
                    </div>
                    <div className="mb-4 text-xs tracking-[0.28em] text-[#FFC000]">{benefit.title}</div>
                    <h3 className="mb-4 text-2xl text-white" style={sectionTitleStyle}>
                      {benefit.headline}
                    </h3>
                    <div className="space-y-2 text-sm leading-7 text-white/68">
                      {benefitDescriptionItems.map((item, index) => (
                        <div key={`${benefit.key}-${index}`}>{item}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-14 border border-white/10 bg-[linear-gradient(135deg,rgba(255,192,0,0.08),rgba(255,255,255,0.02)_42%,rgba(255,192,0,0.03)_100%)] p-8 md:p-10">
              <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div>
                  <div className="mb-4 inline-block border border-[#FFC000]/50 px-4 py-1 text-[11px] tracking-[0.35em] text-[#FFC000]">
                    合作荣誉
                  </div>
                  <h3 className="mb-3 text-3xl text-white md:text-4xl" style={sectionTitleStyle}>
                    合作调酒师荣誉墙
                  </h3>
                  <div className="max-w-2xl text-sm leading-7 text-white/68 md:text-base">
                    呈现合作调酒师在赛事与风味探索中的代表性成绩与作品表达。
                  </div>
                </div>
                <div className="inline-flex w-fit items-center rounded-full border border-[#FFC000]/30 px-4 py-2 text-xs tracking-[0.24em] text-[#FFC000]">
                  荣誉与作品对应展示
                </div>
              </div>

              <div className="grid gap-5">
                <div className="space-y-4">
                  {honorWallItems.map((item, index) => (
                    <div
                      key={`${item?.honor ?? ''}-${index}`}
                      className="grid gap-4 border border-white/10 bg-black/30 p-5 md:grid-cols-[120px_1fr_220px] md:items-center md:p-6"
                    >
                      <div className="text-xs tracking-[0.28em] text-[#FFC000]">荣誉 {`${index + 1}`.padStart(2, '0')}</div>
                      <div>
                        <div className="mb-2 text-lg text-white md:text-xl" style={sectionTitleStyle}>
                          {item?.honor ?? ''}
                        </div>
                        <div className="text-sm leading-7 text-white/62">以东方风味表达回应专业赛事语境，保留品牌合作语义与作品识别度。</div>
                      </div>
                      <div className="border border-[#FFC000]/20 bg-[#141414] px-5 py-4">
                        <div className="mb-3 text-xs tracking-[0.24em] text-white/45">作品</div>
                        <div className="space-y-2">
                          {(item?.works ?? []).map((work, workIndex) => (
                            <div key={`${item?.honor ?? ''}-${workIndex}`} className="text-lg text-white">
                              {work ?? ''}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {(hasApiIssue || programEmpty) && (
              <div className="mt-6 text-sm text-[#d6ba63]">当前共创计划内容已切换为品牌默认展示。</div>
            )}
          </div>
        </section>

        <section id="flavor" className="relative overflow-hidden bg-[#111111] px-5 py-24 md:px-10">
          <div className="absolute inset-y-0 right-0 hidden w-1/2 bg-[radial-gradient(circle_at_center,rgba(255,192,0,0.12),transparent_65%)] lg:block" />
          <div className="mx-auto grid max-w-[1200px] gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div>
              <div className="mb-5 inline-block border border-[#FFC000]/60 px-4 py-1 text-[11px] tracking-[0.35em] text-[#FFC000]">
                风味哲学
              </div>
              <h2 className="mb-6 text-4xl text-white md:text-5xl" style={sectionTitleStyle}>
                用东方发酵语言，重写鸡尾酒的草本层次
              </h2>
              <div className="mb-5 text-base leading-8 text-white/72 md:text-lg">
                元酒梦并不止于传统黄酒表达，而是以更现代、更国际化的方式理解“鲜、甘、润、陈”。
                君系列拥有近似味美思的结构衔接能力，却保留了来自中国黄酒的谷物香、发酵感与回甘。
              </div>
              <div className="text-base leading-8 text-white/72 md:text-lg">
                在马天尼、曼哈顿或高球等经典结构中，它既能成为基底，也能成为修饰；既能放大草本与木质香，也能令酒体更加圆润完整。
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              {[
                {
                  title: '鲜',
                  text: '来自黄酒本身的鲜味结构，为鸡尾酒带来更长的中段与更细腻的尾韵。',
                },
                {
                  title: '润',
                  text: '酒体柔顺，更适合低调内敛的东方美学表达，恰如君子，温润如玉',
                },
                {
                  title: '陈',
                  text: '年份与发酵带来的复杂层次，使其在经典调酒中也能拥有辨识度。',
                },
                {
                  title: '衡',
                  text: '在酸、甜、苦、草本之间建立更平衡的东方语言，适合进行跨文化创作。',
                },
              ].map((item) => (
                <div key={item.title} className="border border-white/10 bg-black/30 p-6">
                  <div className="mb-4 text-3xl text-[#FFC000]" style={sectionTitleStyle}>
                    {item.title}
                  </div>
                  <div className="text-sm leading-7 text-white/68">{item.text}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="buy" className="bg-[#060606] px-5 py-24 md:px-10">
          <div className="mx-auto max-w-[1200px] rounded-[28px] border border-[#FFC000]/20 bg-[linear-gradient(135deg,rgba(255,192,0,0.12),rgba(255,192,0,0.02)_30%,rgba(255,255,255,0.02)_100%)] p-8 md:p-12">
            <div className="mb-12 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="mb-4 inline-block border border-[#FFC000]/60 px-4 py-1 text-[11px] tracking-[0.35em] text-[#FFC000]">
                  购买引导
                </div>
                <h2 className="mb-4 text-4xl text-white md:text-5xl" style={sectionTitleStyle}>
                  在熟悉的平台，开启你的东方风味实验
                </h2>
                <div className="max-w-2xl text-base leading-8 text-white/72 md:text-lg">
                  无论你是正在准备新酒单，还是为酒吧寻找下一款有故事感的黄酒基底，都可以从以下平台快速购买「元酒梦2021君系列」。
                </div>
              </div>
            </div>

            {isRefreshing && linkEmpty ? (
              <div className="grid gap-4 md:grid-cols-3">
                {[1, 2, 3].map((item) => (
                  <Skeleton key={item} className="h-[120px] rounded-[18px] bg-white/10" />
                ))}
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-3">
                {purchaseDisplays.map((item) => {
                  const linkContent = (
                    <>
                      <div>
                        <div className="mb-2 text-xs tracking-[0.28em] text-white/45">购买渠道</div>
                        <div className="text-2xl text-white" style={sectionTitleStyle}>
                          {item.label}
                        </div>
                      </div>
                      <MoveRight className="h-6 w-6 text-[#FFC000] transition-transform group-hover:translate-x-1" />
                    </>
                  );

                  const linkClassName = cn(
                    'group flex min-h-[120px] items-center justify-between rounded-[18px] border border-white/10 bg-black/40 px-6 py-5 transition-all hover:border-[#FFC000]/60 hover:bg-black/60',
                  );

                  if (isInternalPath(item?.url ?? '')) {
                    return (
                      <Link key={item.id} to={item.url} className={linkClassName}>
                        {linkContent}
                      </Link>
                    );
                  }

                  return (
                    <a
                      key={item.id}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className={linkClassName}
                    >
                      {linkContent}
                    </a>
                  );
                })}
              </div>
            )}

            {(hasApiIssue || linkEmpty) && (
              <div className="mt-6 text-sm text-[#d6ba63]">当前购买入口已展示默认平台链接。</div>
            )}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-black px-5 py-16 md:px-10">
        <div className="mx-auto grid max-w-[1200px] gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <div className="mb-3 text-sm tracking-[0.35em] text-[#FFC000]">元酒梦 2021 君系列</div>
            <h2 className="mb-4 text-3xl text-white" style={sectionTitleStyle}>
              为调酒师而生的东方黄酒表达
            </h2>
            <div className="max-w-2xl text-sm leading-7 text-white/62 md:text-base">
              将中国黄酒从餐桌带入酒吧，从传统发酵工艺延伸至现代鸡尾酒创作。元酒梦愿与你一起，为世界酒单写下更具辨识度的东方章节。
            </div>
          </div>

          <div>
            <div className="mb-4 text-sm tracking-[0.28em] text-[#FFC000]">订阅资讯</div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event?.target?.value ?? '')}
                placeholder="请输入邮箱"
                className="h-12 w-full border border-white/15 bg-transparent px-4 text-sm text-white outline-none transition-colors placeholder:text-white/35 focus:border-[#FFC000]"
              />
              <button
                type="button"
                onClick={handleSubscribe}
                className="h-12 shrink-0 bg-[#FFC000] px-6 text-sm font-bold text-black transition-colors hover:bg-[#d4a100]"
              >
                订阅资讯
              </button>
            </div>
            <div className="mt-4 text-xs leading-6 text-white/42">获取新品发布、合作计划与行业活动动态。</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
